import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';

import { Text, Wrapper } from 'widget/components';
import { useIsMutedDark } from 'widget/hooks';
import { useMsgContext } from '../../../msg-context';

import Button from './button/button';
import MiniButton from './mini-button/mini-button';

type PlaybackRate = 1 | 1.5 | 2;

const getPlaybackRate = (playbackRate: PlaybackRate): PlaybackRate => {
  if (playbackRate == 1) return 1.5;
  else if (playbackRate == 1.5) return 2;

  return 1;
};

const getTime = (seconds: number) => {
  const [h, m, s] = moment
    .utc(new Date(0).setSeconds(seconds))
    .format('HH:mm:ss')
    .split(':');

  if (!h && !m && !s) return null;

  return `${parseInt(h) > 0 ? `${h}:` : ''}` + `${m}:` + s;
};

const Audio = () => {
  const { msg } = useMsgContext();

  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState<PlaybackRate>(1);
  const [timer, setTimer] = useState<NodeJS.Timeout>();

  const { t } = useTranslation();
  const isMutedDark = useIsMutedDark();
  const ref = useRef<HTMLAudioElement>(null);

  const handleMiniButtonClick = () => {
    const newPlaybackRate = getPlaybackRate(playbackRate);
    setPlaybackRate(newPlaybackRate);

    if (!ref.current) return;
    ref.current.playbackRate = newPlaybackRate;
  };

  const handlePause = () => {
    setIsPlaying(false);
    ref.current?.pause();
    clearInterval(timer);
  };

  const handlePlay = () => {
    setIsPlaying(true);
    ref.current?.play();

    const newTimer = setInterval(() => {
      if (!ref.current?.currentTime) return;
      setCurrentTime(ref.current.currentTime);
    }, 10);

    setTimer(newTimer);
  };

  const handleButtonClick = () => {
    isPlaying ? handlePause() : handlePlay();
  };

  const color = isMutedDark ? 'white' : 'dark';
  const time = getTime(currentTime);

  return (
    <div className="whatcrm-widget-audio">
      <audio onPause={handlePause} ref={ref} src={msg.file_url} />
      <Button isPlaying={isPlaying} onClick={handleButtonClick} />

      <div style={{ minWidth: 0 }}>
        <Text color={color} fontSize={14} isEllipsis>{t`Voice message`}</Text>

        <Wrapper gap={4}>
          <MiniButton onClick={handleMiniButtonClick}>
            {`${playbackRate}x`}
          </MiniButton>

          {time && (
            <Text color="gray-600" fontSize={12}>
              {time}
            </Text>
          )}
        </Wrapper>
      </div>
    </div>
  );
};

export default Audio;
